import { isAxiosError } from 'axios';

// Split out of EvaluatePerformance.tsx (react-refresh/only-export-components)
// — a pure error-classification helper, not a component. Behavior unchanged.

// S28: extended return shape adds errorCode so the render block can decide CTA
// visibility without re-parsing. isAuth semantics for 401 are PRESERVED.
export interface SubmitErrorResult {
  message: string;
  isAuth: boolean;
  errorCode?: string;
}

// Legacy copy used as fallback for unknown/malformed 402 details.
const LEGACY_BUDGET_COPY = 'Monthly evaluation budget exceeded.';

export function classifySubmitError(error: unknown): SubmitErrorResult {
  if (isAxiosError(error)) {
    const status = error.response?.status;

    // PRESERVED: 401 — session expired / auth required
    if (status === 401) return { message: 'Session expired — please log in again.', isAuth: true };

    // PRESERVED: 429 — concurrency limit (not a 402; no new 409 branch added)
    if (status === 429)
      return {
        message: 'Too many evaluations in progress (max 3). Please wait for one to finish.',
        isAuth: false,
      };

    // S28: 402 — branch on error_code from structured detail.
    // detail may be a dict (structured) OR a plain string (legacy HTTPException).
    // Both shapes are type-guarded — never throws on malformed input.
    if (status === 402) {
      const detail = error.response?.data?.detail;
      const code =
        typeof detail === 'object' && detail !== null
          ? (detail as Record<string, unknown>).error_code
          : undefined;

      if (code === 'no_allocation') {
        return {
          message: 'Your coach has not allocated you credits.',
          isAuth: false,
          errorCode: 'no_allocation',
        };
      }

      if (code === 'credit_balance_zero') {
        return {
          message: 'This channel is out of credits — buy a pack.',
          isAuth: false,
          errorCode: 'credit_balance_zero',
        };
      }

      if (code === 'budget_exceeded') {
        return {
          message: LEGACY_BUDGET_COPY,
          isAuth: false,
          errorCode: 'budget_exceeded',
        };
      }

      // Unknown/missing code: fall back to backend message, then legacy copy.
      const rawMsg =
        typeof detail === 'object' && detail !== null
          ? (detail as Record<string, unknown>).message
          : typeof detail === 'string'
          ? detail
          : undefined;
      const fallbackMessage: string =
        typeof rawMsg === 'string' ? rawMsg : LEGACY_BUDGET_COPY;

      return {
        message: fallbackMessage,
        isAuth: false,
        errorCode: typeof code === 'string' ? code : undefined,
      };
    }

    // PRESERVED: 404 — video not found
    if (status === 404)
      return {
        message: 'Athlete video not found — please re-upload the video.',
        isAuth: false,
      };

    // PRESERVED: string detail passthrough (other non-402 errors with plain string detail)
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return { message: detail, isAuth: false };
  }

  // PRESERVED: generic fallback (covers 409, 500, network errors, etc.)
  return {
    message: 'An unexpected error occurred. Please try again.',
    isAuth: false,
  };
}
